import {useCallback, useMemo} from 'react';
import assert from 'assert-ts';
import {signOutRedirectProps} from 'authProviderProps';
import {
  type AuthContextProps,
  hasAuthParams,
  useAuth,
} from 'react-oidc-context';
import {useSearchParams} from 'react-router';
import {useAppDispatch} from 'store/hooks';
import {REDIRECT_URI_NAME} from 'navigation/consts';
import type {AuthState} from '../types';

type AuthStateProps = Pick<AuthContextProps, 'isLoading' | 'error'> & {
  authState: AuthState;
  login: () => void;
  logout: () => void;
  logoutCallback: () => void;
  canAttemptAutoSignin: () => boolean;
};

export const useAuthState = (): AuthStateProps => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const auth = useAuth();

  const canAttemptAutoSignin = useCallback(() => {
    return (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading
    );
  }, [auth.activeNavigator, auth.isAuthenticated, auth.isLoading]);

  const authState: AuthState = useMemo(() => {
    if (auth.isAuthenticated) {
      const user = assert(
        auth.user,
        'User should be defined when authenticated',
      );

      return {
        status: 'Authenticated',
        user: {
          id: user.profile['cognito:username'] as string,
          email: user.profile.email ?? '',
          name: user.profile.given_name ?? user.profile.name ?? '',
          picture: user?.profile.picture,
        },
      };
    }

    return {
      status: 'Anonymous',
    };
  }, [auth.isAuthenticated, auth.user]);

  const login = useCallback(() => {
    dispatch({
      type: 'RESET_USERS',
      payload: {
        status: 'NotLoaded',
      },
    });

    return auth.signinRedirect({
      url_state: searchParams.get(REDIRECT_URI_NAME) || undefined,
    });
  }, [auth, dispatch, searchParams]);

  const logout = useCallback(() => {
    const {cognitoDomain, clientId, logoutUri} = signOutRedirectProps;

    const signOutRedirect = () => {
      dispatch({
        type: 'RESET_USERS',
        payload: {
          status: 'NotLoaded',
        },
      });

      auth.removeUser();
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    };

    signOutRedirect();
  }, [auth, dispatch]);

  const logoutCallback = useCallback(() => {
    // Probably wont be called anymore because OneLogin handles the logout redirection
  }, []);

  return {
    authState,
    login,
    logout,
    logoutCallback,
    canAttemptAutoSignin,
    ...auth,
  };
};
